import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import mixpanel from 'mixpanel-browser';

// Redux
import { connect } from 'react-redux';

// Actions
import { toggleSideNav, setPage } from '../../actions/navActions';
import { setAlert } from '../../actions/alertActions';
import { createAd, getAdsByPostId } from '../../actions/adActions';
import { getPostById } from '../../actions/postActions';

// Stripe
import { getBoostCheckoutUrl } from "../../config/stripe-config";

// components - material UI
import { Button } from '@material-ui/core';

// Modal
import Modal from '../../components/modal/Modal';
import ModalContainer from '../../components/modal/ModalContainer';

import Layout from '../../components/layout/Layout';

// Icons
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import HelpOutlineOutlinedIcon from '@material-ui/icons/HelpOutlineOutlined';

// Initial State
const initialState = {
    cta_type: 'message',
    cta_url: '',
    cta_label: 'Visit Website',
    run_until_pause: false,
    duration_days: 1,
}

const COST_PER_DAY = 5;
  
const BoostPage = ({
    toggleSideNav,
    setPage,
    setAlert,
    createAd,
    getPostById,
    getAdsByPostId,
    nav: { 
        sideNav 
    },
    post: { 
        post, 
        loading 
    }, 
    auth: {
        isAuthenticated,
        user,
        user_id
    },
    ad,
    match
}) =>  {

    // State variable to track if Mixpanel event has been sent
    const [sentMixpanel, setSentMixpanel] = useState(false);

    // State for form data
    const [formData, setFormData] = useState(initialState);

    const [processing, setProcessing] = useState(false);

    // Tooltip Modal stuff
    const [toolTipModal, setToolTipModal] = useState(false);
    const [toolTipType, setToolTipType] = useState("");

    // -- END: Tooltip Modal stuff

    // Get the current URL and extract the "redirect" parameter
    const url_filter = (window.location.href);
    const url = new URL(url_filter);
    const hostname = window.location.hostname;

    useEffect(() => {
    
        // Set the page to 'detail'
        setPage('boost');

        // Fetch the post data based on the `match.params.id` parameter
        getPostById(match.params.id);

    }, [ match.params.id ]);

    useEffect(() => {
    
        // Fetch the comments for the post if the loading state is `false` and the post data is available
        if(!loading && post) {
            // Get Ads associated with current post
            getAdsByPostId(post._id);
        }

    }, [ loading, post ]);

    // Redirect to post page if already has active or paused ads
    if(ad.ads.length > 0 && (ad.ads[0].status === 'active' || ad.ads[0].status === 'paused' || ad.ads[0].status === 'scheduled' || ad.ads[0].status === 'in review')) {
        localStorage.setItem("trigger_alert", "already_boosted");
        window.location.href = `/post/${match.params.id}`;
    }

    // Destructure the email value from form data
    const { 
        cta_type,
        cta_url,
        cta_label,
        run_until_pause,
        duration_days, 
    } = formData;

    // Function to handle input change
    const onChange = e => setFormData({ ...formData, [e.target.name]: e.target.value});

    // Duration slider
    const handleSliderChange = (e) => {
        setFormData({ ...formData, ["duration_days"]: parseInt(e.target.value)})
    };

    // Create the ad in the DB, upon successful payment
    const handleSubmitAd = async () => {

        console.log("About to create Ad");

        let formData = { // formData
            cta_type,
            cta_url,
            cta_label,
            run_until_pause,
            duration_days, 
            amount_budgeted: COST_PER_DAY*duration_days*100 // in cents
        };

        let userData = null;

        if(user && user_id) {
            userData = { // userData
                _id: user._id,
                username: user.username,
                img: user.img,
                first_name: user.first_name,
                last_name: user.last_name,
                verified: user.verified,
            }
        }


        try {
            // Submit action
            console.log('Creating ad');
            const ad_id = await createAd(post._id, formData, userData);

            console.log(`Done Creating ad - ${ad_id}`);

            return ad_id;

        } catch (error) {
            console.error('Error Creating Ad', error);
        }
    }

    const handleSubmit = async (e) => {

        e.preventDefault();

        console.log("About to create checkout link");

        if(!isAuthenticated) {
            setAlert('Something Went Wrong. Please logout and try again.', 'danger');
            return;
        }

        if(cta_type === 'website' && cta_url === '') {
            setAlert('Website is required.', 'danger');
            return;
        }

        if (cta_type === 'website') {
            try {
                // throws if invalid
                new URL(cta_url);  
            } catch {
                setAlert('Website URL must have https & www.', 'danger');
                return;
            }
        }

        if(cta_type === 'website' && cta_label === '') {
            setAlert('Button label is required.', 'danger');
            return;
        }
        
        // check post exists
        if(post == null || post == undefined || post._id == null) {
            setAlert('Something Went Wrong. Please logout and try again.', 'danger');
            return;
        }

        setProcessing(true);

        try {

            // Create Ad with status 'checkout'
            const ad_id = await handleSubmitAd();

            if(ad_id) {
                console.log("creating checkout link");
                const checkoutUrl = await getBoostCheckoutUrl(duration_days*COST_PER_DAY, ad_id);
                window.location.href = checkoutUrl;
                console.log('redirected');
            } else {
                throw new Error();
            }
            
        } catch (error) {
            console.log('ERROR GETTING CHECKOUT LINK');
            // console.log(error)
            alert('Something Went Wrong. Please logout and try again.')
        }
    }

    const toggleToolTipModal = (type) => {

        // Toggles the resume modal
        setToolTipModal(!toolTipModal); 
        setToolTipType(type);
    }

    // Handles Mixpanel tracking
    const handleMixpanel = () => {
        mixpanel.init(process.env.REACT_APP_MIXPANEL_ID);

        // Track "About Page View" event
        mixpanel.track("Boost Page View");
    }

    // Checks if the environment is production and Mixpanel tracking hasn't been sent
    if(process.env.NODE_ENV === 'production' && !sentMixpanel && !hostname.includes('xbuysell')) {

        // Call Mixpanel tracking function
        handleMixpanel();

        // Set Mixpanel event as sent
        setSentMixpanel(true);
    }

	return (
	  <Fragment>
        <Layout sideNav={sideNav} handleSlideMenu={toggleSideNav} page="boost" >

            {/* Render the GettingStarted component */}
            <p style={{margin: '10px 10px 20px', color: 'rgb(115, 115, 115)'}}>
                Boost your post into an ad to increase its reach.
            </p>

            <div style={{padding: '0 0 4px'}}>
                <hr style={{height: '6px', width: '100%', background: '#f6f6f6'}} />
            </div>

            <h2 style={{margin: '5px 10px', display: 'flex', alignItems: 'center'}}>
                Goal
                <span onClick={() => toggleToolTipModal("goal")} className='icon_20 hover_btn_bg' style={{marginLeft: '5px'}}>
                    <HelpOutlineOutlinedIcon />
                </span>
            </h2>
            <p style={{margin: '0 10px 10px', color: '#000'}}>
                What results would you like from this ad?
            </p>

            <div>
                {/* Option */}
                <div onClick={() => setFormData({ ...formData, ["cta_type"]: "message"})} style={{borderBottom: '1px solid #e6ecf0', height: 'fit-content', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 20px 10px 10px', marginBottom: '10px'}}>
                    <div style={{fontSize: '14px', display: 'flex', flexDirection: 'column', fontColor: 'grey'}}>
                        <span style={{color: '#000', fontWeight: 500}}>
                            Get more messages
                        </span>
                        <span style={{fontSize: '14px', color: 'rgb(115, 115, 115)'}}>
                            Best for getting people to send you a direct message.
                        </span>
                    </div>
                    <div>
                        {cta_type === 'message' ? (
                            <div style={{display:'flex', justifyContent:'center', alignItems:'center',height:'16px', width:'16px', border:'2px solid rgb(29, 155, 240)', padding:'2px', borderRadius:'50%', marginRight:'10px'}}>
                                <div style={{height:'100%', width:'100%', background:'rgb(29, 155, 240)', borderRadius:'50%'}}></div>
                            </div>
                        ) : (
                            <div style={{height:'19px', width:'19px', border:'2px solid #cecece', borderRadius:'50%', marginRight:'10px'}}></div>
                        )}
                    </div>
                </div>
                {/* END Option */}

                {/* Option */}
                <div onClick={() => setFormData({ ...formData, ["cta_type"]: "profile"})} style={{borderBottom: '1px solid #e6ecf0', height: 'fit-content', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 20px 10px 10px', marginBottom: '10px'}}>
                    <div style={{fontSize: '14px', display: 'flex', flexDirection: 'column', fontColor: 'grey'}}>
                        <span style={{color: '#000', fontWeight: 500}}>
                            Get more profile visits
                        </span>
                        <span style={{fontSize: '14px', color: 'rgb(115, 115, 115)'}}>
                            Best for profile awareness
                        </span>
                        {user && (
                            <span style={{fontSize: '14px', color: 'rgb(115, 115, 115)'}}>
                                @{user.username}
                            </span>
                        )}
                    </div>
                    <div>
                        {cta_type === 'profile' ? (
                            <div style={{display:'flex', justifyContent:'center', alignItems:'center',height:'16px', width:'16px', border:'2px solid rgb(29, 155, 240)', padding:'2px', borderRadius:'50%', marginRight:'10px'}}>
                                <div style={{height:'100%', width:'100%', background:'rgb(29, 155, 240)', borderRadius:'50%'}}></div>
                            </div>
                        ) : (
                            <div style={{height:'19px', width:'19px', border:'2px solid #cecece', borderRadius:'50%', marginRight:'10px'}}></div>
                        )}
                    </div>
                </div>
                {/* END Option */}

                {/* Option */}
                <div onClick={() => setFormData({ ...formData, ["cta_type"]: "website"})} style={{borderBottom: `${cta_type !== 'website' ? '1px solid #e6ecf0' : ''}`, height: 'fit-content', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 20px 10px 10px', marginBottom: '10px'}}>
                    <div style={{fontSize: '14px', display: 'flex', flexDirection: 'column', fontColor: 'grey'}}>
                        <span style={{color: '#000', fontWeight: 500}}>
                            Get more website visitors
                        </span>
                        <span style={{fontSize: '14px', color: 'rgb(115, 115, 115)'}}>
                            Best for online sales, bookings and helping people learn more about you
                        </span>
                    </div>
                    <div>
                        {cta_type === 'website' ? (
                            <div style={{display:'flex', justifyContent:'center', alignItems:'center',height:'16px', width:'16px', border:'2px solid rgb(29, 155, 240)', padding:'2px', borderRadius:'50%', marginRight:'10px'}}>
                                <div style={{height:'100%', width:'100%', background:'rgb(29, 155, 240)', borderRadius:'50%'}}></div>
                            </div>
                        ) : (
                            <div style={{height:'19px', width:'19px', border:'2px solid #cecece', borderRadius:'50%', marginRight:'10px'}}></div>
                        )}
                    </div>
                </div>
                {/* Dynamically show input container */}
                {cta_type === 'website' && (
                    <Fragment>
                        <div style={{width: '100%', boxSizing:'border-box', padding:'0 10px 10px', flexDirection: 'column', display:'flex', justifyContent:'center', borderBottom: '1px solid #e6ecf0'}}>
                    
                            {/* Username Input */}
                            <input
                                type="text"
                                placeholder="https://"
                                className="input"
                                name="cta_url"
                                value={cta_url}
                                onChange={e => onChange(e)}
                                maxlength="50"
                                style={{width: '100%', boxSizing: 'border-box'}}
                            />

                            {/* Month */}
                            <div style={{width: '100%', marginRight:'12px', cursor:'pointer', position: 'relative', flexGrow: 2, backgroundColor:'#fff', borderRadius:'4px', border: '1px solid rgb(207, 217, 222)'}}>
                                <label style={{lineHeight:'16px', fontSize:'13px', paddingTop:'8px', color:'rgb(83, 100, 113)', position:'absolute', overflowWrap: 'break-word', paddingLeft:'8px', paddingRight:'8px'}}>
                                    <span>Button label</span>
                                </label>
                                <select 
                                    name="cta_label" 
                                    onChange={e => onChange(e)}
                                    value={cta_label}
                                    style={{width:'100%', paddingLeft:'8px', paddingRight:'8px', paddingTop:'15px', lineHeight:'20px', outline:'none', border:'none', backgroundColor:'rgba(0, 0, 0, 0)', appearance:'none', outlineStyle:'none', color:'rgb(15, 20, 25)', fontSize:'17px', marginTop:'16px', paddingBottom:'8px', cursor:'pointer'}}
                                >
                                    <option value={`Visit Website`}>Visit Website</option>
                                    <option value={`Learn More`}>Learn More</option>
                                    <option value={`Shop Now`}>Shop Now</option>
                                    <option value={`Book Now`}>Book Now</option>
                                    <option value={`Sign Up`}>Sign Up</option>
                                </select>
                                <KeyboardArrowDownIcon style={{top: '50%', right:'12px', marginTop:'-0.75em', height:'1.5em', color:'rgb(83, 100, 113)', position:'absolute'}} />
                            </div>
                        </div>
                    </Fragment>
                )}
                {/* END Option */}
            </div>

            {/* Spacer */}
            <div style={{height: '10px'}}></div>

            <h3 style={{margin: '10px 10px',  display: 'flex', alignItems: 'center'}}>
                What's your budget?
                <span onClick={() => toggleToolTipModal("budget")} className='icon_20 hover_btn_bg' style={{marginLeft: '5px'}}>
                    <HelpOutlineOutlinedIcon />
                </span>
            </h3>
            
            {/* Run daily */}
            <div style={{margin: '10px 10px', padding: '16px', borderRadius: '12px', background: 'rgb(245, 245, 245)'}}>
                <div style={{display: 'flex', justifyContent: 'space-around', alignItems: 'center', gap: '20px'}}>
                    <div>
                        <p style={{fontSize: '20px', fontWeight: 'bold'}}>
                            {run_until_pause ? `$5 daily` : `$${duration_days*COST_PER_DAY} over ${duration_days} day${duration_days == 1 ? '' : 's'}`}
                        </p>
                        <p style={{fontSize: '14px', color: 'rgb(115, 115, 115)'}}>is your Budget</p>
                    </div>
                    <div>
                        <p style={{fontSize: '16px', fontWeight: 'bold'}}>
                            {run_until_pause ? `980 - 2,600` : `590 - 1,500`}
                        </p>
                        <p style={{fontSize: '14px', color: 'rgb(115, 115, 115)'}}>Estimated daily reach</p>
                    </div>
                </div>
            </div>

            <div>
                <h4 style={{margin: '10px 10px', fontWeight: 500}}>
                    How long?
                </h4>

                {/* Option */}
                {/* <div onClick={() => setFormData({ ...formData, ["run_until_pause"]: true})} style={{borderBottom: '1px solid #e6ecf0', height: 'fit-content', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 20px 10px 10px', marginBottom: '10px'}}>
                    <div style={{fontSize: '14px', display: 'flex', flexDirection: 'column', fontColor: 'grey'}}>
                        <span style={{color: '#000'}}>
                            Run this ad until I pause it
                        </span>
                    </div>
                    <div>
                        {run_until_pause ? (
                            <div style={{display:'flex', justifyContent:'center', alignItems:'center',height:'16px', width:'16px', border:'2px solid rgb(29, 155, 240)', padding:'2px', borderRadius:'50%', marginRight:'10px'}}>
                                <div style={{height:'100%', width:'100%', background:'rgb(29, 155, 240)', borderRadius:'50%'}}></div>
                            </div>
                        ) : (
                            <div style={{height:'19px', width:'19px', border:'2px solid #cecece', borderRadius:'50%', marginRight:'10px'}}></div>
                        )}
                    </div>
                </div> */}
                {/* END Option */}

                {/* Option */}
                <div onClick={() => setFormData({ ...formData, ["run_until_pause"]: false})} style={{borderBottom: '1px solid #e6ecf0', height: 'fit-content', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 20px 10px 10px', marginBottom: '10px'}}>
                    <div style={{fontSize: '14px', display: 'flex', flexDirection: 'column', fontColor: 'grey'}}>
                        <span style={{color: '#000'}}>
                            Set duration
                        </span>
                    </div>
                    <div>
                        {!run_until_pause ? (
                            <div style={{display:'flex', justifyContent:'center', alignItems:'center',height:'16px', width:'16px', border:'2px solid rgb(29, 155, 240)', padding:'2px', borderRadius:'50%', marginRight:'10px'}}>
                                <div style={{height:'100%', width:'100%', background:'rgb(29, 155, 240)', borderRadius:'50%'}}></div>
                            </div>
                        ) : (
                            <div style={{height:'19px', width:'19px', border:'2px solid #cecece', borderRadius:'50%', marginRight:'10px'}}></div>
                        )}
                    </div>
                </div>
                
                {/* Dynamically show input container */}
                {!run_until_pause && (
                    <div style={{padding: '10px'}}>
                        <div>
                            <span>Number of days: <b>{duration_days} day{duration_days == 1 ? '' : 's'}</b></span>
                        </div>
                        
                        <div >
                            <input
                                type="range"
                                min="1"
                                max="30"
                                value={duration_days}
                                onChange={handleSliderChange}
                                style={{width: '100%', cursor: 'pointer'}}
                            />
                        </div>
                    </div>
                )}
                {/* END Option */}

            </div>

            <Button onClick={handleSubmit} disabled={processing} variant="outlined" className="sidebar__tweet black" fullWidth>
                {processing ? 'Processing...' : (
                    <Fragment>
                        Boost Post
                        <span style={{width: '5px'}}>
                    
                        </span>
                        <TrendingUpIcon />
                    </Fragment>
                )}
                
            </Button>

            {/* Empty div */}
            <div className="no-rides" />
        </Layout>

        {/* Tooltip Modals */}
        <ModalContainer show={toolTipModal} onClose={toggleToolTipModal}>
            <Modal>

                {toolTipType === 'goal' && (
                    <Fragment>
                        {/* Title + Description */}
                        <h3 style={{fontWeight: 500, marginBottom: '5px'}}>Reach more people</h3>
                        <p>Your ad will appear in feed, search results, and comment sections across our platforms.</p>

                    </Fragment>
                )}

                {toolTipType === 'budget' && (
                    <Fragment>
                        {/* Title + Description */}
                        <h3 style={{fontWeight: 500, marginBottom: '5px'}}>Budget</h3>
                        <p>Your budget affects how many people see your ad. As you increase your ad budget, you grow the amount of people you can reach. Consider your goals.</p>
                        
                        <div style={{height: '15px'}}></div>

                        {/* Title + Description */}
                        <h3 style={{fontWeight: 500, marginBottom: '5px'}}>Duration</h3>
                        <p>Duration determines how long your ad will run for. The longer you run your ad, the more people will see it.</p>
                    </Fragment>
                )}
            </Modal>
        </ModalContainer>
    </Fragment>
	);
  }
  
BoostPage.propTypes = {
    toggleSideNav: PropTypes.func.isRequired,
    setPage: PropTypes.func.isRequired,
    setAlert: PropTypes.func.isRequired,
    createAd: PropTypes.func.isRequired,
    getPostById: PropTypes.func.isRequired,
    getAdsByPostId: PropTypes.func.isRequired,
    nav: PropTypes.object.isRequired,
    auth: PropTypes.object.isRequired,
    ad: PropTypes.object.isRequired,
}

const mapStateToProps = state => ({

    // Mapping the states from the Redux store to the below props
    nav: state.nav,
    post: state.post,
    auth: state.auth,
    ad: state.ad
});

export default connect(mapStateToProps, { 

    // Connecting actions to the component
    toggleSideNav, 
    setPage,
    setAlert,
    createAd,
    getPostById,
    getAdsByPostId
})(BoostPage);
  